import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { format, isValid } from 'date-fns';
import { cloneDeep } from 'es-toolkit';

import { Box, FormField, FormHelp, Paragraph, Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  DatarouterError,
  useRentalManagerUserPropertySpecialOfferWebApiHandler_create,
  useRentalManagerUserPropertySpecialOfferWebApiHandler_update,
} from '@zg-rentals/rental-manager-env';
import { debounce } from '@zg-rentals/zdash';

import PropertiesActions from '../../actions/PropertiesActions';
import actions from '../../containers/CreateListingPage/actions';
import { useSelector } from '../../selectors/useSelector';
import clickstreamUtils from '../../utils/clickstreamUtils';
import { isValidDateStringFormatMMDDYYYY } from '../../utils/dateUtils';

import { TRIGGER_LOCATIONS } from '../../constants/ClickstreamConstants';
import { GET_LISTING_RECS } from '../../constants/RecommendationsConstants';
import { SPECIAL_OFFERS_ACTIONS } from '../../constants/SpecialOffersConstants';
import TimeConstants from '../../constants/TimeConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../constants/WizardConstants';
import SpecialOffersCSEvents from '../../model/ClickstreamEvent/SpecialOffersCSEvents';
import SpecialOffer from '../../model/SpecialOffer';
import WizardServerError from '../../model/WizardServerError';

import { ModularListingContext } from '../../containers/CreateListingPage/context';

import DatePickerWithDateSelect from '../DatePickerWithDateSelect';
import RMWInput from '../RMWInput';
import WizardAlert from '../WizardAlert';

import type { ChangeEvent } from 'react';
import type { SpecialOfferProps } from '../../model/SpecialOffer';

const handleDebounce = (func: (e: ChangeEvent<HTMLInputElement>) => void, wait: number) => debounce(func, wait);

type ErrorProps = { name: string; message: string; errorCode: number };

type Props = {
  action?: string;
  defaultOffer?: SpecialOfferProps;
  onClose?: null | (() => void);
  isMultifamilyForm?: boolean;
};

const SpecialOfferFormField = ({
  action = SPECIAL_OFFERS_ACTIONS.ADD,
  defaultOffer = {
    description: '',
    startDateYyyyMmDd: '',
    endDateYyyyMmDd: '',
    offerIdEncoded: '',
  },
  onClose = null,
  isMultifamilyForm = false,
}: Props) => {
  const dispatch = useDispatch();

  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, isSavingSpecialOffers },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    properties: {
      currentProperty,
      currentProperty: { active, dateLastActivatedTimestamp, listingAlias, specialOffers },
    },
    user,
  } = useSelector((state) => state);

  let formAction = action;
  let formOffer = defaultOffer;
  if (!isMultifamilyForm && specialOffers.length > 0) {
    formAction = SPECIAL_OFFERS_ACTIONS.EDIT;
    formOffer = specialOffers[0];
  }
  // represent the start/end date before formatting for BE
  const [specialOffer, setSpecialOffer] = useState(SpecialOffer(formOffer));
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [description, setDescription] = useState(specialOffer.description);
  const [errorObj, setErrorObj] = useState<{ [key: string]: string }>({});

  const { startDateYyyyMmDd, endDateYyyyMmDd, description: initialDescription, offerIdEncoded } = specialOffer;

  useEffect(() => {
    // track first time user clicks to add a special offer on single family listings
    if (specialOffers.length === 0 && !isMultifamilyForm) {
      const triggerLocationSuffix = clickstreamUtils.getListingStatusSuffix(active, dateLastActivatedTimestamp);
      SpecialOffersCSEvents.SpecialOffersAddNew({
        currentProperty,
        user,
        triggerLocation: `${TRIGGER_LOCATIONS.POSTING_PATH_REVIEW}${triggerLocationSuffix}`,
      });
    }
  }, []);

  useEffect(() => {
    // Check if there are any validation errors
    const hasErrors = Object.values(errorObj).some((error) => error != null && error !== '');
    // Check if dates have valid values
    const hasValidStartDate = startDate === '' || isValid(new Date(startDate));
    const hasValidEndDate = endDate === '' || isValid(new Date(endDate));

    // Save should be disabled if:
    // - Description is empty
    // - There are validation errors
    // - Start date or end date are invalid
    const shouldDisableSave = description === '' || hasErrors || !hasValidStartDate || !hasValidEndDate;

    actions.updateIsSaveDisabled({ reducerDispatch, isSaveDisabled: shouldDisableSave });

    return () => {
      actions.updateIsSaveDisabled({ reducerDispatch, isSaveDisabled: false });
    };
  }, [description, errorObj, startDate, endDate, reducerDispatch]);

  useEffect(() => {
    if (isSaveComplete || isSavingSpecialOffers) {
      save();
    }
  }, [isSaveComplete, isSavingSpecialOffers]);

  const handleSuccess = (response: SpecialOfferProps) => {
    const specialOffersCopy = cloneDeep(specialOffers);

    if (formAction === SPECIAL_OFFERS_ACTIONS.EDIT) {
      const indexToUpdate = specialOffersCopy.findIndex(
        (offer: SpecialOfferProps) => offer.offerIdEncoded === offerIdEncoded,
      );

      if (indexToUpdate === -1) {
        return setErrorObj({
          ...errorObj,
          save: 'There was an error saving your offer. Please try again.',
        });
      }

      specialOffersCopy[indexToUpdate] = SpecialOffer(response);
    } else {
      specialOffersCopy.push(SpecialOffer(response));
    }

    dispatch(PropertiesActions.updateCurrentProperty({ specialOffers: specialOffersCopy }));
    if (isSaveComplete) {
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
    }

    if (isSavingSpecialOffers) {
      actions.updateAnyProperty({
        reducerDispatch,
        data: {
          isSavingSpecialOffers: false,
        },
      });
    }

    queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
    if (onClose) {
      onClose();
    }
  };

  const handleError = ({ error, contextName }: { error: ErrorProps; contextName: string }) => {
    const message = `Special Offer Error @ ${error.name} where code is ${error.errorCode}`;
    logError({
      error,
      context: {
        contextName,
        message,
      },
    });

    if (isInModalState) {
      updateServerError(error.message, error.name);
    } else {
      enqueueToast(
        <Toast tone="critical">{`There was an error ${formAction === SPECIAL_OFFERS_ACTIONS.ADD ? 'creating' : 'updating'} the offer.`}</Toast>,
      );
    }

    errorObj[error.name] = error.message;

    if (isSavingSpecialOffers) {
      actions.updateAnyProperty({
        reducerDispatch,
        data: {
          isSavingSpecialOffers: false,
        },
      });
    }
  };

  const { mutate: createSpecialOffer } = useRentalManagerUserPropertySpecialOfferWebApiHandler_create({
    onSuccess: handleSuccess,
    onError: (err) => {
      const { errors } = DatarouterError.getErrorDetails(err);

      errors.forEach((error) => {
        handleError({ error, contextName: `PropertiesActions.createSpecialOffer: listingAlias: ${listingAlias}` });
      });
    },
  });

  const { mutate: updateSpecialOffer } = useRentalManagerUserPropertySpecialOfferWebApiHandler_update({
    onSuccess: handleSuccess,
    onError: (err) => {
      const { errors } = DatarouterError.getErrorDetails(err);

      errors.forEach((error) => {
        handleError({ error, contextName: 'PropertiesActions.updateSpecialOffer' });
      });
    },
  });

  const updateServerError = (message = '', fieldName = 'startDate') => {
    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt: DISPLAY_SERVER_ERROR_AT.FIELD,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setErrorObj((prev) => ({ ...prev, description: '' }));
    clearServerError();
    setDescription(e.target.value);
    setSpecialOffer({ ...specialOffer, description: e.target.value });
  };

  const handleChangeDebounced = handleDebounce(handleChange, TimeConstants.SELECTION_DEBOUNCE_TIME_MS);

  const validateDateRange = (startDateValue: string, endDateValue: string) => {
    if (!(startDateValue && endDateValue && isValid(new Date(startDateValue)) && isValid(new Date(endDateValue)))) {
      return;
    }

    const start = new Date(startDateValue);
    const end = new Date(endDateValue);

    if (end < start) {
      setErrorObj((prev) => ({
        ...prev,
        endDate: 'End date must be after the start date.',
      }));
    }
  };

  const handleStartDateChange = (date: string) => {
    setErrorObj((prev) => ({ ...prev, startDate: '', endDate: '' }));
    setStartDate(date);
    clearServerError();

    // Validate date format
    if (date && !isValidDateStringFormatMMDDYYYY(date)) {
      setErrorObj((prev) => ({
        ...prev,
        startDate: 'Start date must be in MM/DD/YYYY format.',
      }));
      return;
    }

    validateDateRange(date, endDate);
  };

  const handleEndDateChange = (date: string) => {
    setErrorObj((prev) => ({ ...prev, endDate: '' }));
    setEndDate(date);
    clearServerError();

    // Validate date format
    if (date && !isValidDateStringFormatMMDDYYYY(date)) {
      setErrorObj((prev) => ({
        ...prev,
        endDate: 'End date must be in MM/DD/YYYY format.',
      }));
      return;
    }

    validateDateRange(startDate, date);
  };

  const save = () => {
    // Format and cross-field validations are already handled in onChange handlers
    // Save button is disabled when there are any validation errors
    const specialOfferCopy = cloneDeep(specialOffer);
    if (isValid(new Date(startDate))) {
      specialOfferCopy.startDateYyyyMmDd = format(new Date(startDate), 'yyyy-MM-dd');
    } else {
      specialOfferCopy.startDateYyyyMmDd = '';
    }

    if (isValid(new Date(endDate))) {
      specialOfferCopy.endDateYyyyMmDd = format(new Date(endDate), 'yyyy-MM-dd');
    } else {
      specialOfferCopy.endDateYyyyMmDd = '';
    }

    setSpecialOffer({
      ...specialOfferCopy,
    });

    const postData = {
      ...specialOfferCopy,
      listingAlias,
    };

    if (formAction === SPECIAL_OFFERS_ACTIONS.EDIT) {
      updateSpecialOffer({ postData });
    } else {
      createSpecialOffer({ postData });
    }
  };

  return (
    <div
      data-testid="SpecialOfferFormField"
      data-eventid={
        specialOffers.length === 0 && !isMultifamilyForm
          ? SpecialOffersCSEvents.SpecialOffersAddNew.data_event_id
          : undefined
      }
    >
      {isInModalState && <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />}

      <Paragraph css={{ marginBottom: '400' }}>
        Share details of the deal or discount you&#39;d like to display to renters. This will appear on your listing
        until your chosen end date.
      </Paragraph>

      <FormField
        error={!!errorObj.startDate}
        description={<FormHelp error={!!errorObj.startDate}>{errorObj.startDate}</FormHelp>}
      >
        <DatePickerWithDateSelect
          label="Offer start date"
          onDateChange={handleStartDateChange}
          showNextMonthButton={false}
          initialDate={startDateYyyyMmDd}
          setDefaultDate={false}
          defaultPastDateToToday={false}
          saveOnDateSelect={false}
        />
      </FormField>

      <Box css={{ marginBlock: '200' }}>
        <FormField
          error={!!errorObj.endDate}
          description={<FormHelp error={!!errorObj.endDate}>{errorObj.endDate}</FormHelp>}
        >
          <DatePickerWithDateSelect
            label="Offer end date"
            onDateChange={handleEndDateChange}
            showNextMonthButton={false}
            initialDate={endDateYyyyMmDd}
            setDefaultDate={false}
            minDate={startDateYyyyMmDd || undefined}
            defaultPastDateToToday={false}
            saveOnDateSelect={false}
          />
        </FormField>
      </Box>

      <RMWInput
        data-testid="description-textarea"
        error={!!errorObj.description}
        isRequired={true}
        errorMessage={errorObj.description}
        description="Your offer won’t post if it includes contact info - phone numbers, urls, or email addresses"
        fieldName="description"
        flex="1"
        initialValue={initialDescription || ''}
        inputType="textarea"
        label="Description"
        maxLength="255"
        placeholder="Example: Get $500 off your first month’s rent when you sign by October 15."
        showCharacterCount
        onBlur={(e: ChangeEvent<HTMLInputElement>) => {
          clearServerError();
          setErrorObj((prev) => ({ ...prev, description: '' }));
          setDescription(e.target.value);
          setSpecialOffer({ ...specialOffer, description: e.target.value });
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChangeDebounced(e)}
      />
    </div>
  );
};

export default SpecialOfferFormField;
